import { useEffect, useState, useCallback } from 'react';
import {
    ChevronRight, Plus, ClipboardCheck, Lock, Check,
    ArrowRightLeft, CalendarDays, BarChart3,
} from 'lucide-react';
import { TypBadge } from './PokladnaPrehledTab';
import NabidkaAkci from '../../components/layout/NabidkaAkci';
import PruhSPrejezdem from '../../components/layout/PruhSPrejezdem';
import FlekIkona from './FlekIkona';
import { fmtKc, barvaFleku } from './flekyGrid';
import ZpetTlacitko from '../../components/ui/ZpetTlacitko';

const ACCENT = '#3B82F6';

/**
 * Čtyři záložky, ne pět. „Nastavení fleku" (název, pořadí, počáteční částka)
 * není něco, co se dělá za provozu — patří k položce Konfigurace ve skupině
 * Napříč pokladnami. Páté ouško navíc na 390 px lezlo za hranu displeje.
 */
const ZALOZKY = [
    { id: 'pohyby', label: 'Pohyby', Icon: ArrowRightLeft },
    { id: 'smeny', label: 'Směny', Icon: CalendarDays },
    { id: 'audity', label: 'Audity', Icon: ClipboardCheck },
];

const STAVY = {
    otevreno: { label: 'otevřeno', barva: '#10B981' },
    ceka_na_schvaleni: { label: 'čeká na schválení', barva: '#F59E0B' },
    uzavreno: { label: 'uzavřeno', barva: null },
};

/**
 * Co z fleku odchází. Stejný seznam jako v accessoru
 * `StgPokladnaSmena::koncovy_stav_ocekavany_kc` — kdyby se rozešly, sloupec
 * by ukazoval jiný pohyb peněz než napočítaný zůstatek vedle něj.
 *
 * Bez znaménka vypadá výplata dýška stejně jako příjem z objednávky a řádek
 * pak lže o směru — zrovna u pokladny je to ta jediná věc, kterou z výpisu
 * čteme.
 */
const VYDAJE = new Set([
    'odvod_do_hlavniho', 'vyplata_z_hlavniho', 'prevod_z_hlavniho',
    'vyber_majitel', 'zaloha_mzdy', 'hotovostni_vydaj', 'korekce_manko',
    'korekce_karta_minus', 'proplaceni_nakupu_vydej', 'odvod_do_banky',
]);

/**
 * Prostřední sloupec — detail jednoho fleku.
 *
 * Hlavička nese **lištu akcí**: co se s flekem dá udělat, stojí u něj, ne
 * v dashboardu nad seznamem. Dřív se akce sbíraly na přehledu a platily pro
 * „hlavní flek" nebo „ten, co zrovna vyšel z API" — z tlačítka nebylo poznat,
 * čeho se chytne.
 *
 * Nabídka akcí se řídí typem a stavem: rozvozový flek se schvaluje
 * (vyúčtování rozvozce), hlavní se uzavírá (uzávěrka manažera).
 */
export default function FlekDetailPanel({
    flek, isDark, cardStyle, mutedColor, isMobile = false,
    zalozka, setZalozka, onZpet,
    onPridatPohyb, onAudit, onUzavrit, onSchvalit, rezervaDole = null,
    // Uzavřený flek přepočítává jen správce (admin, CEO) — 2026-11-02.
    auditUzavrenych = false,
    // Když se třetí sloupec nevejde (úzké okno, mobil), statistika se sem
    // přestěhuje jako první záložka. Norma chce, aby se obsah panelu
    // zopakoval v detailu; nad seznamem pohybů by ale sedmisetpixelová karta
    // jen odsunula to, kvůli čemu sem člověk šel, takže je vedle nich.
    statistika = null,
}) {
    if (!flek) return null;
    const stav = STAVY[flek.smena?.stav] ?? null;
    const hranice = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const sekceBarva = barvaFleku(flek);
    const pill = {
        background: isDark ? 'rgba(255,255,255,0.04)' : 'var(--surface-base)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
    };

    const akce = [];
    if (flek.smena?.stav === 'otevreno') {
        akce.push({ id: 'pohyb', label: 'Přidat pohyb', Icon: Plus, onClick: () => onPridatPohyb?.(flek) });
        akce.push({ id: 'audit', label: 'Audit', Icon: ClipboardCheck, onClick: () => onAudit?.(flek) });
        akce.push({
            id: 'uzavrit',
            label: flek.typ === 'bistro_hlavni' ? 'Uzávěrka' : 'Uzavřít flek',
            Icon: Lock, onClick: () => onUzavrit?.(flek), primarni: true,
        });
    } else if (flek.smena?.stav === 'ceka_na_schvaleni') {
        akce.push({ id: 'schvalit', label: 'Schválit vyúčtování', Icon: Check, onClick: () => onSchvalit?.(flek), primarni: true });
    } else if (auditUzavrenych) {
        akce.push({ id: 'audit', label: 'Audit', Icon: ClipboardCheck, onClick: () => onAudit?.(flek) });
    }

    // Kdo to je — miniatura, název a pilulky. Stejná kostra jako
    // `MaterialDetailHeader`: na mobilu menší ikona, hlavička tam nescrolluje
    // pryč, takže se počítá každý pixel výšky.
    const telo = (
        <div className="flex items-start gap-3 min-w-0 flex-1">
            <FlekIkona flek={flek} size={isMobile ? 42 : 58} radius={isMobile ? 8 : 9} />
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-bold flex-shrink-0"
                        style={{ background: `${sekceBarva}1f`, color: sekceBarva }}>
                        {flek.typ === 'bistro_hlavni' ? 'BISTRO' : 'ROZVOZ'}
                    </span>
                    <span className="text-[17px] font-bold truncate" style={{ color: 'var(--text-primary)' }}>{flek.nazev}</span>
                </div>
                {/* Hotovost je jediné číslo, kvůli kterému se pokladna otevírá —
                    patří jí velikost, ne pilulka mezi pilulkami. Cockpit ji ve
                    slotu „Pokladna" ukazuje stejně velkou. */}
                <div className="flex items-baseline gap-2 flex-wrap" style={{ marginTop: 3 }}>
                    <span className="font-bold tabular-nums" style={{ fontSize: 26, lineHeight: 1.1, color: 'var(--text-primary)' }}>
                        {fmtKc(flek.hotovost_kc)}
                    </span>
                    <span className="text-[12px]" style={{ color: mutedColor }}>v šuplíku</span>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap mt-2">
                    {stav && (
                        <span className="px-2 py-1 rounded-lg text-[12px] font-medium"
                            style={{ ...pill, color: stav.barva ?? 'var(--text-primary)' }}>{stav.label}</span>
                    )}
                    <span className="px-2 py-1 rounded-lg text-[12px] font-medium"
                        style={{ ...pill, color: flek.drzitel ? 'var(--text-primary)' : mutedColor }}>
                        {flek.drzitel ?? 'volný'}
                    </span>
                    {!flek.aktivni && (
                        <span className="px-2 py-1 rounded-lg text-[12px] font-medium"
                            style={{ ...pill, color: '#EF4444' }}>neaktivní</span>
                    )}
                </div>
            </div>
        </div>
    );

    // Kolik akcí se vejde a co spadne pod „…", řeší sdílená komponenta —
    // stejně jako v Dokladech, ať se hlavičky detailů nerozejdou.
    const tlacitka = <NabidkaAkci akce={akce} isMobile={isMobile} isDark={isDark} barva={ACCENT} kolikVidet={2} />;

    return (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'}
            style={isMobile ? null : cardStyle}>
            {/* Hlavička + lišta akcí */}
            {isMobile ? (
                <div className="flex flex-col" style={{ padding: '8px 12px 10px', gap: 8, flexShrink: 0, borderBottom: `1px solid ${hranice}` }}>
                    <div className="flex items-center gap-2">
                        <ZpetTlacitko onClick={onZpet} popis="Zpět na seznam" />
                        <div className="flex items-center gap-1.5" style={{ marginLeft: 'auto', flexShrink: 0 }}>{tlacitka}</div>
                    </div>
                    {telo}
                </div>
            ) : (
                <div className="flex items-start justify-between p-4" style={{ borderBottom: `1px solid ${hranice}` }}>
                    {telo}
                    <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>{tlacitka}</div>
                </div>
            )}

            {/* Podzáložky */}
            <PruhSPrejezdem className="flex items-center gap-1 px-2 overflow-x-auto" style={{ borderBottom: `1px solid ${hranice}` }}>
                {(statistika ? [{ id: 'statistika', label: 'Statistika', Icon: BarChart3 }, ...ZALOZKY] : ZALOZKY).map(z => {
                    const aktivni = zalozka === z.id;
                    return (
                        <button key={z.id} onClick={() => setZalozka(z.id)}
                            className="flex items-center gap-1.5 text-[12.5px] font-medium"
                            style={{
                                padding: '9px 10px',
                                borderBottom: `2px solid ${aktivni ? ACCENT : 'transparent'}`,
                                color: aktivni ? ACCENT : mutedColor,
                            }}>
                            <z.Icon size={13} /> {z.label}
                        </button>
                    );
                })}
            </PruhSPrejezdem>

            {/* Rezerva na plovoucí lištu — bez ní se k poslednímu obsahu nedá dorolovat. */}
            <div className="flex-1 overflow-y-auto" style={{ paddingBottom: rezervaDole || undefined }}>
                {/* Šířka jako třetí sloupec (320 + odsazení) — roztažená přes
                    celý detail by z karty byl plakát a kruh by plaval uprostřed. */}
                {statistika && zalozka === 'statistika' && (
                    <div className="p-3" style={{ maxWidth: 376 }}>{statistika}</div>
                )}
                {zalozka === 'pohyby' && <Pohyby flek={flek} isDark={isDark} mutedColor={mutedColor} />}
                {zalozka === 'smeny' && <Smeny flek={flek} isDark={isDark} mutedColor={mutedColor} />}
                {zalozka === 'audity' && <Audity flek={flek} isDark={isDark} mutedColor={mutedColor} />}
            </div>
        </div>
    );
}

/** Načtení seznamu pro záložku — jeden tvar pro všechny tři. */
function useSeznam(url) {
    const [data, setData] = useState(null);
    const nacti = useCallback(() => {
        fetch(url, { credentials: 'same-origin' })
            .then(r => r.ok ? r.json() : Promise.reject(r.status))
            .then(d => setData(Array.isArray(d) ? d : (d?.data ?? [])))
            .catch(() => setData([]));
    }, [url]);
    useEffect(() => { setData(null); nacti(); }, [nacti]);
    return data;
}

function Prazdno({ text, mutedColor }) {
    return <div className="px-4 py-10 text-center text-[12px]" style={{ color: mutedColor }}>{text}</div>;
}

function Pohyby({ flek, isDark, mutedColor }) {
    const pohyby = useSeznam(`/api/finance/pokladna/pohyby?pokladna_id=${flek.id}`);
    if (pohyby == null) return <Prazdno text="Načítám…" mutedColor={mutedColor} />;
    if (!pohyby.length) return <Prazdno text="Na tomhle fleku dnes žádný pohyb nebyl" mutedColor={mutedColor} />;
    const hranice = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)';
    return (
        <div>
            {pohyby.map(p => {
                const vydaj = VYDAJE.has(p.typ);
                return (
                    <div key={p.id} className="flex items-center gap-3 text-[12.5px]"
                        style={{ padding: '8px 12px', borderBottom: `1px solid ${hranice}` }}>
                        <span className="tabular-nums flex-shrink-0" style={{ width: 44, color: mutedColor }}>
                            {String(p.created_at ?? '').slice(11, 16)}
                        </span>
                        <span className="flex-shrink-0"><TypBadge typ={p.typ} /></span>
                        <span className="truncate flex-1 min-w-0" style={{ color: mutedColor }}>
                            {p.objednavka?.cislo_objednavky ?? p.poznamka ?? ''}
                        </span>
                        <span className="tabular-nums font-semibold flex-shrink-0"
                            style={{ color: vydaj ? '#EF4444' : 'var(--text-primary)' }}>
                            {vydaj ? '−' : '+'}{fmtKc(p.castka_kc)}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

function Smeny({ flek, isDark, mutedColor }) {
    const smeny = useSeznam(`/api/finance/pokladna/smeny?pokladna_id=${flek.id}`);
    if (smeny == null) return <Prazdno text="Načítám…" mutedColor={mutedColor} />;
    if (!smeny.length) return <Prazdno text="Flek zatím žádnou směnu neměl" mutedColor={mutedColor} />;
    const hranice = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)';
    return (
        <div>
            {smeny.map(s => {
                const stav = STAVY[s.stav] ?? null;
                const rozdil = Number(s.manko_prebytek_kc ?? 0);
                return (
                    <div key={s.id} className="flex items-center gap-3 text-[12.5px]"
                        style={{ padding: '8px 12px', borderBottom: `1px solid ${hranice}` }}>
                        <span className="tabular-nums flex-shrink-0" style={{ width: 74, color: mutedColor }}>
                            {String(s.datum ?? '').slice(0, 10)}
                        </span>
                        <span className="truncate flex-1 min-w-0" style={{ color: 'var(--text-primary)' }}>
                            {s.zamestnanec ? `${s.zamestnanec.jmeno} ${s.zamestnanec.prijmeni}` : 'volná'}
                        </span>
                        {stav && (
                            <span className="text-[11px] flex-shrink-0" style={{ color: stav.barva ?? mutedColor }}>
                                {stav.label}
                            </span>
                        )}
                        <span className="tabular-nums font-semibold flex-shrink-0" style={{
                            width: 90, textAlign: 'right',
                            color: rozdil === 0 ? mutedColor : (rozdil > 0 ? '#10B981' : '#EF4444'),
                        }}>
                            {rozdil === 0 ? '—' : `${rozdil > 0 ? '+' : ''}${fmtKc(rozdil)}`}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

function Audity({ flek, isDark, mutedColor }) {
    const audity = useSeznam(`/api/finance/pokladna/audity?pokladna_id=${flek.id}`);
    if (audity == null) return <Prazdno text="Načítám…" mutedColor={mutedColor} />;
    if (!audity.length) return <Prazdno text="Flek ještě nikdo neauditoval" mutedColor={mutedColor} />;
    const hranice = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)';
    return (
        <div>
            {audity.map(a => {
                const rozdil = Number(a.rozdil_kc ?? 0);
                return (
                    <div key={a.id} className="text-[12.5px]"
                        style={{ padding: '9px 12px', borderBottom: `1px solid ${hranice}` }}>
                        <div className="flex items-center gap-3">
                            <span className="tabular-nums flex-shrink-0" style={{ width: 74, color: mutedColor }}>
                                {String(a.datum ?? '').slice(0, 10)}
                            </span>
                            <span className="flex-1 min-w-0 truncate" style={{ color: mutedColor }}>
                                očekáváno {fmtKc(a.ocekavany_stav_kc)} · napočítáno {fmtKc(a.skutecny_stav_kc)}
                            </span>
                            <span className="tabular-nums font-semibold flex-shrink-0" style={{
                                color: rozdil === 0 ? '#10B981' : (rozdil > 0 ? '#F59E0B' : '#EF4444'),
                            }}>
                                {rozdil > 0 ? '+' : ''}{fmtKc(rozdil)}
                            </span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
